import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  Chip,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Alert,
  Pagination,
  CircularProgress,
  Container
} from "@mui/material";
import {
  Add,
  Search,
  MoreVert,
  Visibility,
  Edit,
  Delete,
  ShoppingCart,
  CheckCircle,
  Pending,
  Inventory,
  PlaylistAddCheck,
  Clear,
} from "@mui/icons-material";
import { purchaseAPI } from "../../services/API";
import { useSnackbar } from "notistack";
import { useAuth } from "../../contexts/AuthContext";

export const Purchase = () => {
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("");

  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
  });

  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { hasAnyRole } = useAuth();

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchPurchaseOrders();
    }, 300);

    return () => clearTimeout(timer);
  }, [pagination.page, pagination.limit, searchTerm]);

  const fetchPurchaseOrders = async () => {
    try {
      setLoading(true);
      setSearchLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...(searchTerm && { search: searchTerm }),
      };

      const response = await purchaseAPI.getAll(params);
      const { purchaseOrders: ordersData, pagination: paginationData } =
        response.data.data;

      setPurchaseOrders(ordersData || []);
      setPagination((prev) => ({
        ...prev,
        total: paginationData.total,
        pages: paginationData.pages,
      }));
    } catch (error) {
      enqueueSnackbar("Error fetching purchase orders", { variant: "error" });
    } finally {
      setLoading(false);
      setSearchLoading(false);
    }
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (event, value) => {
    setPagination((prev) => ({ ...prev, page: value }));
  };

  const handleMenuOpen = (event, order) => {
    setAnchorEl(event.currentTarget);
    setSelectedOrder(order);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleView = () => {
    if (selectedOrder) {
      navigate(`/hospital/pharmacy/purchases/${selectedOrder._id}`);
      setSelectedOrder(null);
    }
    setAnchorEl(null);
  };

  const handleEdit = () => {
    if (selectedOrder) {
      navigate(`/hospital/pharmacy/purchases/${selectedOrder._id}/edit`);
      setSelectedOrder(null);
    }
    setAnchorEl(null);
  };

  const handleReceive = () => {
    if (selectedOrder) {
      navigate(`/hospital/pharmacy/purchases/${selectedOrder._id}/receive`);
      handleMenuClose();
    }
  };

  const handlePartialReceive = () => {
    if (selectedOrder) {
      navigate(`/hospital/pharmacy/purchases/${selectedOrder._id}/receive?partial=true`);
      handleMenuClose();
    }
  };

  const handleStatusUpdate = () => {
    if (selectedOrder) {
      setSelectedStatus(selectedOrder.status);
      setStatusDialogOpen(true);
      handleMenuClose();
    }
  };

  const handleDelete = () => {
    if (selectedOrder) {
      setDeleteDialogOpen(true);
      handleMenuClose();
    }
  };

  const updateOrderStatus = async () => {
    if (!selectedOrder || !selectedOrder._id) {
      enqueueSnackbar("No order selected", { variant: "error" });
      return;
    }

    try {
      await purchaseAPI.updateStatus(selectedOrder._id, {
        status: selectedStatus,
      });
      enqueueSnackbar("Order status updated successfully", {
        variant: "success",
      });
      setStatusDialogOpen(false);
      setSelectedOrder(null);
      fetchPurchaseOrders();
    } catch (error) {
      enqueueSnackbar("Error updating order status", { variant: "error" });
    }
  };

  const deleteOrder = async () => {
    if (!selectedOrder || !selectedOrder._id) {
      enqueueSnackbar("No order selected", { variant: "error" });
      return;
    }

    try {
      await purchaseAPI.delete(selectedOrder._id);
      enqueueSnackbar("Purchase order deleted successfully", {
        variant: "success",
      });
      setDeleteDialogOpen(false);
      setSelectedOrder(null);
      fetchPurchaseOrders();
    } catch (error) {
      enqueueSnackbar("Error deleting purchase order", { variant: "error" });
    }
  };

  const handleStatusDialogClose = () => {
    setStatusDialogOpen(false);
    setSelectedOrder(null);
  };

  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
    setSelectedOrder(null);
  };

  const canEditOrder = (order) => {
    return order && ["draft", "sent"].includes(order.status);
  };

  const canDeleteOrder = (order) => {
    return order && order.status === "draft";
  };

  const canReceiveOrder = (order) => {
    return (
      order &&
      ["confirmed", "sent", "partially_received"].includes(order.status)
    );
  };

  const canPartiallyReceiveOrder = (order) => {
    return (
      order &&
      ["confirmed", "sent", "partially_received"].includes(order.status)
    );
  };

  const canUpdateStatus = (order) => {
    return (
      order && order.status !== "completed" && order.status !== "cancelled"
    );
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: "default",
      sent: "info",
      confirmed: "primary",
      partially_received: "warning",
      completed: "success",
      cancelled: "error",
    };
    return colors[status] || "default";
  };

  const getStatusIcon = (status) => {
    const icons = {
      completed: <CheckCircle />,
      confirmed: <CheckCircle />,
      partially_received: <PlaylistAddCheck />,
      sent: <Pending />,
      draft: <Pending />,
      cancelled: <Pending />,
    };
    return icons[status] || <Pending />;
  };

  return (
    <Box sx={{ minHeight: "100vh", background: "linear-gradient(180deg, #f9fafc 0%, #f2f5f8 100%)" }}>
      <Container maxWidth="xl" sx={{ py: 5 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4 }}>
          <Typography variant="h3" fontWeight="bold" sx={{ fontSize: { xs: '2rem', md: '2.5rem' } }}>
            Purchase Orders
          </Typography>
          {hasAnyRole(["admin", "inventory_manager"]) && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => navigate("/hospital/pharmacy/purchases/new")}
              sx={{ 
                borderRadius: 2, 
                boxShadow: 2, 
                transition: "0.3s", 
                "&:hover": { transform: "scale(1.03)" },
                fontSize: '1.1rem',
                py: 1.5,
                px: 3
              }}
            >
              New Purchase Order
            </Button>
          )}
        </Box>

        {/* Summary Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {[
            { label: "Total Orders", value: pagination.total, icon: <ShoppingCart color="primary" sx={{ fontSize: 50 }} /> },
            { label: "Completed", value: purchaseOrders.filter((o) => o.status === "completed").length, color: "success.main" },
            { label: "Partial Received", value: purchaseOrders.filter((o) => o.status === "partially_received").length, color: "warning.main" },
            { label: "In Progress", value: purchaseOrders.filter((o) => ["sent", "confirmed"].includes(o.status)).length, color: "info.main" },
            { label: "Draft", value: purchaseOrders.filter((o) => o.status === "draft").length },
          ].map((card, idx) => (
            <Grid item xs={12} sm={6} md={2.4} key={idx}>
              <Card sx={{ borderRadius: 3, boxShadow: 3, textAlign: "center", transition: "0.3s", "&:hover": { boxShadow: 6, transform: "translateY(-4px)" } }}>
                <CardContent sx={{ textAlign: "center", py: 3 }}>
                  {card.icon || null}
                  <Typography variant="h3" fontWeight="bold" sx={{ 
                    color: card.color || "text.primary", 
                    mt: 1,
                    fontSize: { xs: '2rem', md: '2.5rem' }
                  }}>
                    {card.value}
                  </Typography>
                  <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                    {card.label}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Search Card */}
        <Card sx={{ mb: 3, borderRadius: 3, boxShadow: 2 }}>
          <CardContent sx={{ py: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Search by PO number or supplier name..."
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  InputProps={{
                    sx: { fontSize: '1.1rem' },
                    startAdornment: (
                      <InputAdornment position="start">
                        {searchLoading ? <CircularProgress size={24} /> : <Search sx={{ fontSize: 28 }} />}
                      </InputAdornment>
                    ),
                    endAdornment: searchTerm && (
                      <InputAdornment position="end">
                        <IconButton
                          size="large"
                          onClick={handleClearSearch}
                          edge="end"
                        >
                          <Clear sx={{ fontSize: 28 }} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" color="text.secondary" align="center" sx={{ fontSize: '1.2rem' }}>
                  Total: {pagination.total} orders
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Purchase Orders Table */}
        <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
          <CardContent sx={{ py: 3 }}>
            <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ fontSize: '1.5rem', mb: 3 }}>
              Purchase Orders
            </Typography>
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={60} />
              </Box>
            ) : (
              <>
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>PO #</TableCell>
                        <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Supplier</TableCell>
                        <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Date</TableCell>
                        <TableCell align="right" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Amount</TableCell>
                        <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Status</TableCell>
                        <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {purchaseOrders.map((order) => (
                        <TableRow key={order._id} hover sx={{ "&:hover": { backgroundColor: "#fafafa" } }}>
                          <TableCell sx={{ py: 2 }}>
                            <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.1rem' }}>
                              {order.poNumber}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 2 }}>
                            <Typography variant="body1" sx={{ fontSize: '1.1rem' }}>
                              {order.supplier?.name}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 2 }}>
                            <Typography variant="body1" sx={{ fontSize: '1.1rem' }}>
                              {new Date(order.createdAt).toLocaleDateString()}
                            </Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ py: 2 }}>
                            <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.1rem' }}>
                              ₹{order.grandTotal?.toFixed?.(2) ?? order.grandTotal}
                            </Typography>
                          </TableCell>
                          <TableCell align="center" sx={{ py: 2 }}>
                            <Chip 
                              label={order.status} 
                              size="medium" 
                              color={getStatusColor(order.status)} 
                              sx={{ fontSize: '1rem', height: '32px' }}
                            />
                          </TableCell>
                          <TableCell align="center" sx={{ py: 2 }}>
                            <IconButton 
                              size="large" 
                              onClick={(e) => handleMenuOpen(e, order)}
                              sx={{ fontSize: '1.5rem' }}
                            >
                              <MoreVert fontSize="inherit" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {purchaseOrders.length === 0 && (
                  <Box sx={{ textAlign: "center", py: 6 }}>
                    <Typography variant="h4" color="text.secondary" gutterBottom sx={{ fontSize: '1.5rem', mb: 2 }}>
                      {searchTerm ? "No purchase orders found matching your search" : "No purchase orders found"}
                    </Typography>
                    {hasAnyRole(["admin", "inventory_manager"]) && (
                      <Button 
                        variant="contained" 
                        startIcon={<Add />} 
                        onClick={() => navigate("/hospital/pharmacy/purchases/new")} 
                        sx={{ 
                          borderRadius: 2,
                          fontSize: '1.1rem',
                          py: 1.5,
                          px: 3
                        }}
                      >
                        Create Purchase Order
                      </Button>
                    )}
                  </Box>
                )}

                {pagination.pages > 1 && (
                  <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                    <Pagination 
                      count={pagination.pages} 
                      page={pagination.page} 
                      onChange={handlePageChange} 
                      color="primary" 
                      showFirstButton 
                      showLastButton 
                      size="large"
                    />
                  </Box>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Action Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          PaperProps={{
            sx: { 
              minWidth: 200,
              '& .MuiMenuItem-root': {
                fontSize: '1.1rem',
                py: 1.5
              }
            }
          }}
        >
          <MenuItem onClick={handleView}>
            <Visibility sx={{ mr: 2, fontSize: 24 }} />
            View Details
          </MenuItem>

          {hasAnyRole(["admin", "inventory_manager"]) && (
            <MenuItem
              onClick={handleEdit}
              disabled={!canEditOrder(selectedOrder)}
            >
              <Edit sx={{ mr: 2, fontSize: 24 }} />
              Edit Order
            </MenuItem>
          )}

          {hasAnyRole(["admin", "inventory_manager"]) && (
            <MenuItem
              onClick={handleReceive}
              disabled={!canReceiveOrder(selectedOrder)}
            >
              <CheckCircle sx={{ mr: 2, fontSize: 24 }} />
              Receive Complete
            </MenuItem>
          )}

          {hasAnyRole(["admin", "inventory_manager"]) && (
            <MenuItem
              onClick={handlePartialReceive}
              disabled={!canPartiallyReceiveOrder(selectedOrder)}
            >
              <PlaylistAddCheck sx={{ mr: 2, fontSize: 24 }} />
              Receive Partial
            </MenuItem>
          )}

          {hasAnyRole(["admin", "inventory_manager"]) && (
            <MenuItem
              onClick={handleStatusUpdate}
              disabled={!canUpdateStatus(selectedOrder)}
            >
              <Inventory sx={{ mr: 2, fontSize: 24 }} />
              Update Status
            </MenuItem>
          )}

          {hasAnyRole(["admin", "inventory_manager"]) && (
            <MenuItem
              onClick={handleDelete}
              disabled={!canDeleteOrder(selectedOrder)}
              sx={{ color: "error.main", fontSize: '1.1rem' }}
            >
              <Delete sx={{ mr: 2, fontSize: 24 }} />
              Delete Order
            </MenuItem>
          )}
        </Menu>

        {/* Status Dialog */}
        <Dialog open={statusDialogOpen} onClose={handleStatusDialogClose} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontSize: '1.5rem', py: 2 }}>
            Update Order Status
          </DialogTitle>
          <DialogContent>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 2, fontSize: '1.1rem' }}>
              PO: {selectedOrder?.poNumber}
            </Typography>
            <FormControl fullWidth>
              <InputLabel sx={{ fontSize: '1.1rem' }}>Status</InputLabel>
              <Select 
                value={selectedStatus} 
                label="Status" 
                onChange={(e) => setSelectedStatus(e.target.value)}
                sx={{ fontSize: '1.1rem' }}
              >
                <MenuItem value="draft" sx={{ fontSize: '1.1rem' }}>Draft</MenuItem>
                <MenuItem value="sent" sx={{ fontSize: '1.1rem' }}>Sent</MenuItem>
                <MenuItem value="confirmed" sx={{ fontSize: '1.1rem' }}>Confirmed</MenuItem>
                <MenuItem value="cancelled" sx={{ fontSize: '1.1rem' }}>Cancelled</MenuItem>
                <MenuItem value="completed" sx={{ fontSize: '1.1rem' }}>Completed</MenuItem>
              </Select>
            </FormControl>
            {selectedStatus === "completed" && (
              <Alert severity="info" sx={{ mt: 2, fontSize: '1.1rem', '& .MuiAlert-message': { fontSize: '1.1rem' } }}>
                Note: Marking as completed will require receiving goods through the receive process.
              </Alert>
            )}
          </DialogContent>
          <DialogActions sx={{ py: 2 }}>
            <Button 
              onClick={handleStatusDialogClose} 
              sx={{ fontSize: '1.1rem', py: 1 }}
            >
              Cancel
            </Button>
            <Button 
              onClick={updateOrderStatus} 
              variant="contained" 
              disabled={!selectedStatus || !selectedOrder || selectedStatus === selectedOrder?.status}
              sx={{ fontSize: '1.1rem', py: 1 }}
            >
              Update Status
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Dialog */}
        <Dialog open={deleteDialogOpen} onClose={handleDeleteDialogClose} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontSize: '1.5rem', py: 2 }}>
            Delete Purchase Order
          </DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ 
              mb: 2, 
              fontSize: '1.1rem', 
              '& .MuiAlert-message': { fontSize: '1.1rem' } 
            }}>
              This action cannot be undone. This will permanently delete the purchase order.
            </Alert>
            <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
              Are you sure you want to delete purchase order <strong>{selectedOrder?.poNumber}</strong>?
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 1, fontSize: '1.1rem' }}>
              Supplier: {selectedOrder?.supplier?.name}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
              Total Amount: ₹{selectedOrder?.grandTotal?.toFixed?.(2) ?? selectedOrder?.grandTotal}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ py: 2 }}>
            <Button 
              onClick={handleDeleteDialogClose} 
              sx={{ fontSize: '1.1rem', py: 1 }}
            >
              Cancel
            </Button>
            <Button 
              onClick={deleteOrder} 
              variant="contained" 
              color="error" 
              disabled={!selectedOrder}
              sx={{ fontSize: '1.1rem', py: 1 }}
            >
              Delete Order
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};